import React from 'react';
import { CheckCircle, MessageCircle, Trophy } from 'lucide-react';

interface FinalSuccessScreenProps {
  questions: any[];
  answeredQuestions: number;
  onResetInterview: () => void;
}

const FinalSuccessScreen: React.FC<FinalSuccessScreenProps> = ({
  questions,
  answeredQuestions,
  onResetInterview
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 welcome-screen">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-10 text-center mx-auto max-w-3xl">
        <div className="mb-10">
          <div className="w-32 h-32 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg">
            <Trophy className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            Interview Successfully Completed!
          </h1>
          <p className="text-gray-600 text-xl mb-10 leading-relaxed">
            Thank you for completing your knowledge transfer interview. Your
            responses have been recorded and will be processed for the
            knowledge transfer documentation.
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-8 border border-blue-200">
            <div className="text-3xl font-bold text-blue-600 mb-3">
              {questions.length}
            </div>
            <div className="text-blue-800 font-medium">Questions Completed</div>
          </div>
          <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-8 border border-green-200">
            <div className="text-3xl font-bold text-green-600 mb-3">
              {answeredQuestions}
            </div>
            <div className="text-green-800 font-medium">Responses Provided</div>
          </div>
          <div className="bg-gradient-to-r from-purple-50 to-pink-100 rounded-xl p-8 border border-green-200">
            <div className="text-3xl font-bold" style={{ color: "#bb1ccc" }}>
              100%
            </div>
            <div className="font-medium" style={{ color: "#bb1ccc" }}>
              Progress Complete
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-gray-50 rounded-xl p-8 mb-10 text-left border border-gray-200">
          <h3 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
            What happens next?
          </h3>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
              <span className="text-gray-700 text-lg leading-relaxed">
                Your responses will be analyzed and processed by our AI system
              </span>
            </li>
            <li className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
              <span className="text-gray-700 text-lg leading-relaxed">
                A comprehensive knowledge transfer document will be generated
              </span>
            </li>
            <li className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
              <span className="text-gray-700 text-lg leading-relaxed">
                You'll receive a copy for review and validation within 24 hours
              </span>
            </li>
            <li className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
              <span className="text-gray-700 text-lg leading-relaxed">
                The final document will be shared with your successor and team
              </span>
            </li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={onResetInterview}
            className="inline-flex items-center gap-3 px-8 py-4 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-all duration-200 transform hover:scale-105"
          >
            Start New Interview
          </button>
          <button
            style={{ background: "linear-gradient(135deg, #bb1ccc 0%, #d946ef 100%)" }}
            className="inline-flex items-center gap-3 px-8 py-4 text-white rounded-xl font-semibold hover:opacity-90 transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            <MessageCircle className="w-5 h-5" />
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinalSuccessScreen;